import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useSelector } from '@xstate/react';

import {
  Alert,
  BodyText,
  Box,
  Button,
  Card,
  DateTimePicker,
  Modal,
  Portal,
  RadioButton,
} from '@chargify/custom-ui-components';
import { formatDate } from '@chargify/formatters';
import { useToastMessages } from '@chargify/toasts';

import { useServices, useSubscriptionResumeService } from '../../../../hooks';
import { resumeSubscriptionSelectors } from '../../../../machines/resumeSubscription';
import { getSubscriptionResumeAlertKeys } from './utils';
import {
  AutomaticResumeOption,
  OptionsWrapper,
} from '../../../../components/RadioOptions';
import { subscriptionsSelectors } from '../../../../machines';

type ResumeContentProps = {
  /**
   * Modal display mode
   */
  displayMode: 'layer' | 'window';
  /**
   * Close event handler
   */
  onClose: () => void;
  /**
   * Subscription id
   */
  subscriptionId: number;
  /**
   * Catalog id
   */
  catalogId: number;
  /**
   * Automatic resume default date
   */
  autoResumeDefaultDate?: string;
};

const ResumeOptions = {
  IMMEDIATE: 'immediate',
  AUTOMATIC_RESUME: 'automatic_resume',
} as const;

const Content = ({
  onClose,
  displayMode,
  subscriptionId,
  catalogId,
  autoResumeDefaultDate = dayjs().add(7, 'day').toISOString(),
}: ResumeContentProps) => {
  const { subscriptionsService } = useServices();
  const { subscription } = useSelector(
    subscriptionsService,
    subscriptionsSelectors.getSubscriptionById(catalogId, subscriptionId)
  );

  const [selectedResumeOption, setSelectedResumeOption] = useState<
    (typeof ResumeOptions)[keyof typeof ResumeOptions]
  >(
    subscription.automaticallyResumeAt
      ? ResumeOptions.AUTOMATIC_RESUME
      : ResumeOptions.IMMEDIATE
  );
  const [resumeDate, setResumeDate] = useState(
    subscription.automaticallyResumeAt || autoResumeDefaultDate
  );

  const { t } = useTranslation(['subscriptions-manager']);

  const resumeService = useSubscriptionResumeService(catalogId, subscriptionId);
  const toastService = useToastMessages();

  const modalRef = useRef(null);
  const portalContainer = useRef(null);

  const resumptionConfig = useSelector(
    resumeService,
    resumeSubscriptionSelectors.getResumptionConfig
  );

  const error = useSelector(
    resumeService,
    resumeSubscriptionSelectors.getErrorCode
  );

  const { resumeSubscriptionSuccess, resumeSubscriptionLoading } = useSelector(
    resumeService,
    resumeSubscriptionSelectors.getResumeSubscriptionStatus
  );

  const {
    product: { name },
  } = subscription;

  useEffect(() => {
    if (subscription.snapDay) {
      resumeService.send('GET_RESUMPTION_CONFIG');
    }
  }, [subscription.snapDay, resumeService]);

  useEffect(() => {
    if (resumeSubscriptionSuccess) {
      onClose();

      const toastMessage =
        selectedResumeOption === ResumeOptions.AUTOMATIC_RESUME
          ? t('pause_subscription.your_plan_is_on_hold_until_date', {
              date: formatDate(resumeDate),
            })
          : t('resume_subscription.resume_plan_confirmation');

      toastService.show({
        level: 'success',
        message: toastMessage,
      });
    }
  }, [resumeSubscriptionSuccess]);

  const onResume = () => {
    if (selectedResumeOption === ResumeOptions.IMMEDIATE) {
      return resumeService.send('RESUME_SUBSCRIPTION');
    }
    return resumeService.send({
      type: 'RESUME_SUBSCRIPTION_AT',
      resumeDate,
    });
  };

  const isSubscriptionResumable = dayjs(
    subscription.automaticallyResumeAt
  ).isAfter(new Date());

  const alertMessageTitle = () => {
    return selectedResumeOption === 'immediate'
      ? t('resume_subscription.plan_will_be_active', { subscriptionName: name })
      : t('resume_subscription.plan_will_be_activated', {
          subscriptionName: name,
        });
  };

  const alertMessageDescription = getSubscriptionResumeAlertKeys({
    isPastRenewalDate: isSubscriptionResumable,
    resumeOption: selectedResumeOption,
    resumptionConfig,
  });

  return (
    <>
      <Card
        border={displayMode === 'layer' ? 'sharp' : 'rounded'}
        ref={modalRef}
      >
        <Card.Header>
          <Modal.Title
            onClose={onClose}
            title={t('resume_subscription.resume_plan')}
          />
        </Card.Header>
        <Card.Body>
          {error ? (
            <Alert
              color="danger"
              title={t(
                'resume_subscription.we_were_unable_to_resume_subscription_title'
              )}
            >
              <Alert.TextMessage>
                {t(
                  error.errorCode === 'NotEligible'
                    ? 'resume_subscription.24_hour_resume_subscription_error'
                    : 'resume_subscription.we_were_unable_to_resume_subscription'
                )}
              </Alert.TextMessage>
            </Alert>
          ) : (
            <>
              <Box marginBottom={6}>
                <Box marginBottom={5}>
                  <BodyText>
                    {t('resume_subscription.resume_subscription_now', {
                      subscriptionName: name,
                    })}
                  </BodyText>
                </Box>
                <OptionsWrapper>
                  <RadioButton
                    label={t('resume_subscription.resume_immediately')}
                    value={ResumeOptions.IMMEDIATE}
                    onValueChange={() =>
                      setSelectedResumeOption(ResumeOptions.IMMEDIATE)
                    }
                    isSelected={
                      selectedResumeOption === ResumeOptions.IMMEDIATE
                    }
                  />
                  <AutomaticResumeOption>
                    <RadioButton
                      label={t('resume_subscription.automatic_resume_option')}
                      value={ResumeOptions.AUTOMATIC_RESUME}
                      onValueChange={() =>
                        setSelectedResumeOption(ResumeOptions.AUTOMATIC_RESUME)
                      }
                      isSelected={
                        selectedResumeOption === ResumeOptions.AUTOMATIC_RESUME
                      }
                    />
                    <DateTimePicker
                      onChange={(date) => setResumeDate(date.toISOString())}
                      modalRef={modalRef}
                      minDate={dayjs().toDate()}
                      portalContainer={portalContainer}
                      initialDate={resumeDate}
                      disabled={
                        selectedResumeOption !== ResumeOptions.AUTOMATIC_RESUME
                      }
                    />
                  </AutomaticResumeOption>
                </OptionsWrapper>
              </Box>
              <Alert title={alertMessageTitle()}>
                <Alert.TextMessage>
                  {t(alertMessageDescription.accessDateInfo, {
                    date: formatDate(resumeDate),
                  })}{' '}
                  <Trans t={t} i18nKey={alertMessageDescription.chargeInfo} />
                </Alert.TextMessage>
              </Alert>
            </>
          )}
        </Card.Body>
        <Card.Footer>
          <Button.Group alignment="right">
            <Button
              color="secondary"
              label={t('resume_subscription.keep_on_hold')}
              onClick={onClose}
              disabled={resumeSubscriptionLoading}
            />
            {!error && (
              <Button
                label={t('resume_subscription.resume')}
                isLoading={resumeSubscriptionLoading}
                onClick={onResume}
              />
            )}
          </Button.Group>
        </Card.Footer>
      </Card>
      <Portal>
        <div ref={portalContainer}></div>
      </Portal>
    </>
  );
};

export default Content;
